import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from './AdminLayout';
import { Tag, Plus, Trash2, Loader2, X, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  code: '',
  discount_type: 'percent',
  amount: '',
  expires_at: '',
  max_redemptions: '',
};

function formatDiscount(coupon) {
  if (coupon.percent_off) return `${coupon.percent_off}% off`;
  if (coupon.amount_off) return `£${(coupon.amount_off / 100).toFixed(2)} off`;
  return '—';
}

function formatExpiry(ts) {
  if (!ts) return 'Never';
  return new Date(ts * 1000).toLocaleDateString('en-GB');
}

export default function AdminPromoCodes() {
  const { authHeaders } = useAdminAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/promo-codes`, { headers: authHeaders });
      setCodes(res.data);
    } catch (e) {
      setToast({ ok: false, msg: e?.response?.data?.detail || 'Could not load promo codes. Is Stripe configured?' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleCreate = async () => {
    if (!form.code.trim() || !form.amount) { setFormError('Code and discount amount are required.'); return; }
    setCreating(true);
    setFormError('');
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        amount: parseFloat(form.amount),
        expires_at: form.expires_at ? Math.floor(new Date(form.expires_at).getTime() / 1000) : null,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
      };
      await axios.post(`${API}/admin/promo-codes`, payload, { headers: authHeaders });
      setShowModal(false);
      setForm(EMPTY_FORM);
      setToast({ ok: true, msg: `Promo code ${payload.code} created.` });
      load();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Failed to create code.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Deactivate promo code "${code}"?`)) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`, { headers: authHeaders });
      setToast({ ok: true, msg: `"${code}" deactivated.` });
      setCodes(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      setToast({ ok: false, msg: e?.response?.data?.detail || 'Failed to deactivate.' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Tag size={20} className="text-blue-500" /> Promo Codes
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">Stripe promotion codes — customers enter these at checkout</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors" title="Refresh">
              <RefreshCw size={16} />
            </button>
            <button
              onClick={() => { setShowModal(true); setFormError(''); setForm(EMPTY_FORM); }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              <Plus size={15} /> Create Code
            </button>
          </div>
        </div>

        {toast && (
          <div className={`flex items-center justify-between gap-2 text-sm px-4 py-3 rounded-lg ${
            toast.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            <span className="flex items-center gap-2">
              {toast.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              {toast.msg}
            </span>
            <button onClick={() => setToast(null)} className="opacity-60 hover:opacity-100"><X size={14} /></button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={22} className="animate-spin text-blue-500" />
            </div>
          ) : codes.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Tag size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No active promo codes. Create one above.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Discount</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden sm:table-cell">Redeemed</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Expires</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {codes.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-semibold text-gray-900">{c.code}</td>
                    <td className="px-4 py-3 text-gray-700">{formatDiscount(c.coupon)}</td>
                    <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">
                      {c.times_redeemed}{c.max_redemptions ? ` / ${c.max_redemptions}` : ''}
                    </td>
                    <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{formatExpiry(c.expires_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(c.id, c.code)}
                        disabled={deletingId === c.id}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Deactivate"
                      >
                        {deletingId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Create Promo Code</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100">
                <X size={17} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={e => setF('code', e.target.value.toUpperCase())}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="SUMMER25"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Discount type</label>
                  <select
                    value={form.discount_type}
                    onChange={e => setF('discount_type', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="percent">% off</option>
                    <option value="fixed">£ off (pence)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {form.discount_type === 'percent' ? 'Percent (e.g. 20)' : 'Pence (e.g. 1000)'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.amount}
                    onChange={e => setF('amount', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={form.discount_type === 'percent' ? '20' : '1000'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Expiry date (optional)</label>
                  <input
                    type="date"
                    value={form.expires_at}
                    onChange={e => setF('expires_at', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Max redemptions</label>
                  <input
                    type="number"
                    min="1"
                    value={form.max_redemptions}
                    onChange={e => setF('max_redemptions', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Unlimited"
                  />
                </div>
              </div>
            </div>

            {formError && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2.5 rounded-lg">
                <AlertCircle size={15} /> {formError}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
